import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import StopIcon from "@mui/icons-material/Stop";
import { Box, IconButton, TextField, Tooltip } from "@mui/material";
import { memo, useCallback, useEffect, useRef, useState } from "react";

export const ScoutInput = memo(function ScoutThreadInput({
  onSubmit,
  onAbort,
  isSending,
  placeholder = "Chat with Scout…",
  autoFocus = false,
}: {
  onSubmit: (message: string) => Promise<void>;
  onAbort?: () => void;
  isSending: boolean;
  placeholder?: string;
  autoFocus?: boolean;
}) {
  const [hasContent, setHasContent] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const isSubmitDisabled = !hasContent;

  const focusInput = useCallback(() => {
    requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
  }, []);

  const clearInput = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.value = "";
      inputRef.current.dispatchEvent(new Event("input", { bubbles: true }));
    }
    setHasContent(false);
  }, []);

  useEffect(() => {
    if (autoFocus) {
      focusInput();
    }
  }, [autoFocus, focusInput]);

  const handleSubmit = useCallback(async () => {
    const value = inputRef.current?.value ?? "";
    if (!value.trim() || isSending) return;
    clearInput();
    focusInput();
    try {
      await onSubmit(value.trim());
    } finally {
      focusInput();
    }
  }, [isSending, onSubmit, clearInput, focusInput]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newHasContent = !!e.target.value.trim();
    setHasContent((prev) => (prev !== newHasContent ? newHasContent : prev));
  }, []);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        handleSubmit();
      } else if (event.key === "Escape") {
        event.preventDefault();
        if (isSending && onAbort) {
          onAbort();
          return;
        }
        const textarea = inputRef.current;
        if (textarea && textarea.value.trim()) {
          clearInput();
          textarea.focus();
        } else if (textarea) {
          textarea.blur();
        }
      }
    },
    [handleSubmit, clearInput, isSending, onAbort]
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        alignItems: "flex-end",
        gap: 1,
        width: "100%",
        maxWidth: 920,
        mx: "auto",
        px: 2,
        py: 1.25,
        borderRadius: (theme) => theme.spacing(3),
        border: 1,
        borderColor: "divider",
        bgcolor: "background.paper",
      }}
    >
      <TextField
        multiline
        minRows={1}
        maxRows={8}
        fullWidth
        variant="standard"
        InputProps={{ disableUnderline: true }}
        onChange={handleChange}
        placeholder={placeholder}
        onKeyDown={handleKeyDown}
        inputRef={inputRef}
        inputProps={{
          "aria-label": "Message Scout",
          style: {
            padding: "4px 0",
            minHeight: 24,
            lineHeight: 1.5,
          },
        }}
        sx={{
          flex: 1,
          minWidth: 0,
          "& .MuiInputBase-root": {
            alignItems: "flex-start",
          },
        }}
      />
      {isSending && onAbort ? (
        <Tooltip title="Stop generating (Esc)" arrow>
          <span style={{ display: "inline-flex", flexShrink: 0, cursor: "pointer" }}>
            <IconButton onClick={onAbort} aria-label="Stop generating" color="primary">
              <StopIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </span>
        </Tooltip>
      ) : (
        <Tooltip title="Send (Enter · Shift+Enter for new line)" arrow>
          <span
            style={{
              display: "inline-flex",
              flexShrink: 0,
              cursor: isSubmitDisabled ? "default" : "pointer",
            }}
          >
            <IconButton
              onClick={handleSubmit}
              onMouseDown={(event) => event.preventDefault()}
              disabled={isSubmitDisabled}
              aria-label="Send message"
              color="primary"
            >
              <ArrowUpwardIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </span>
        </Tooltip>
      )}
    </Box>
  );
});
ScoutInput.displayName = "ScoutThreadInput";
